import classNames from "classnames";
import * as React from "react";
import { createPortal } from "react-dom";

import {
  StatusBadgePill,
  type StatusBadgeSpec,
} from "@/shared/components/StatusBadge";
import { Badge, Popover } from "@/shared/foundation/primitives";

import styles from "./ExplainedBadge.module.css";

/**
 * What the badge draws: a status from `STATUS_BADGES`, optionally with its
 * label replaced, or a category label (such as who owns something), drawn as
 * an outlined `Badge` with no tone.
 */
export type ExplainedBadgeLook =
  | {
      /** An entry of `STATUS_BADGES`. */
      status: StatusBadgeSpec;
      /** Replaces the status's own label. */
      label?: string;
    }
  | {
      status?: never;
      /** A category label, not a status. */
      label: string;
      /** `brand` fills it with Roboto's magenta, as for Beta. Defaults to an outlined label. */
      variant?: "label" | "brand";
    };

export type ExplainedBadgeProps = ExplainedBadgeLook & {
  /** Heads the popup. Defaults to `label`. */
  title?: string;
  /**
   * What the badge means: text, paragraphs, lists or links. Its first element
   * is the answer, in full colour; the lines after it are muted.
   */
  explanation: React.ReactNode;
  /**
   * Buttons or links in the popup's footer, along its bottom edge. Content
   * that depends on what the explanation loads goes there through
   * `ExplainedBadgeFooter`.
   */
  actions?: React.ReactNode;
  /** The badge's accessible name. Defaults to `label`. */
  "aria-label"?: string;
  /** Places the badge within its container. */
  className?: string;
  /** Set on the badge; the popup gets `<id>-popover`. */
  "data-testid"?: string;
  /**
   * Whether hovering opens the popup. Defaults to true. Turn it off where
   * many badges sit together, as in a table, so moving the pointer across
   * them does not open (and fetch for) each one.
   */
  openOnHover?: boolean;
  /** Called when the popup opens or closes. */
  onOpenChange?: (open: boolean) => void;
};

/**
 * Keeps a click inside the badge or its popup from reaching a clickable row
 * around it. The popup is portalled, but React still bubbles its events
 * through the component tree.
 */
function stopPropagation(event: React.SyntheticEvent) {
  event.stopPropagation();
}

/** Where `ExplainedBadgeFooter` puts its children: the open popup's footer. */
const FooterSlot = React.createContext<HTMLElement | null>(null);

/**
 * A status (or label) badge that explains itself: clicking it, focusing it
 * and pressing Enter, or (unless `openOnHover` is off) hovering it opens a
 * popup saying what it means, with optional actions. The popup's content mounts only while
 * it is open.
 */
export function ExplainedBadge(props: ExplainedBadgeProps) {
  const {
    status,
    title,
    explanation,
    actions,
    "aria-label": ariaLabel,
    className,
    "data-testid": testId,
    openOnHover = true,
    onOpenChange,
  } = props;
  const label =
    props.status === undefined
      ? props.label
      : (props.label ?? props.status.label);
  const [footer, setFooter] = React.useState<HTMLDivElement | null>(null);
  return (
    <span
      className={classNames(styles.root, className)}
      onClick={stopPropagation}
      onDoubleClick={stopPropagation}
      onMouseDown={stopPropagation}
      onContextMenu={stopPropagation}
    >
      <Popover
        onOpenChange={
          onOpenChange === undefined ? undefined : (open) => onOpenChange(open)
        }
      >
        <Popover.Trigger
          openOnHover={openOnHover}
          delay={300}
          className={styles.trigger}
          aria-label={ariaLabel ?? label}
          data-testid={testId}
        >
          {status === undefined ? (
            <Badge variant={props.variant ?? "label"}>{label}</Badge>
          ) : (
            <StatusBadgePill status={status} label={label} />
          )}
        </Popover.Trigger>
        <Popover.Content
          className={styles.popup}
          side="bottom"
          data-testid={testId === undefined ? undefined : `${testId}-popover`}
        >
          <div className={styles.body}>
            <Popover.Title>{title ?? label}</Popover.Title>
            <FooterSlot.Provider value={footer}>
              <Popover.Description
                render={<div />}
                className={styles.explanation}
              >
                {explanation}
              </Popover.Description>
            </FooterSlot.Provider>
          </div>
          {/* Outside Popover.Description, so screen readers don't announce
              its buttons as part of the popup's description. */}
          <div ref={setFooter} className={styles.footer}>
            {actions}
          </div>
        </Popover.Content>
      </Popover>
    </span>
  );
}

/**
 * Renders its children in the popup's footer, after any `actions`, from
 * anywhere inside `explanation`: for buttons that depend on what the
 * explanation loads. Renders nothing outside an `ExplainedBadge`.
 */
export function ExplainedBadgeFooter({ children }: React.PropsWithChildren) {
  const slot = React.useContext(FooterSlot);
  return slot === null ? null : createPortal(children, slot);
}
